/**
 * Pure gating logic for sound effects — no DOM, no `Audio`, fully unit-testable.
 *
 * Three separate problems live here, and they are all about the same complaint:
 * "ten orcs attacking sound like ten copies of one file playing at once".
 *
 * 1. **Retrigger cooldown.** Several attackers land their swings on the same
 *    frame. Firing the cue once per attacker produces a flam, not a hit. A cue
 *    that fired less than `cooldownMs` ago is simply dropped.
 * 2. **Concurrency cap.** Even spaced out, a cue that overlaps itself more than
 *    two or three times stops reading as a sound and starts reading as noise.
 * 3. **Distance.** A mob fighting at the edge of the screen should be quieter
 *    than the one biting the hero. The farm knows both positions, so the mixing
 *    is a pure function of the gap between them.
 */

export interface CueGateOpts {
  /** Ignore a repeat of the same cue fired within this many ms. */
  cooldownMs?: number;
  /** Never let more than this many copies of ONE cue sound at once. */
  maxConcurrent?: number;
}

export interface CueGate {
  /** May `cue` start now? Records the play when it says yes. */
  tryPlay(cue: string, now: number): boolean;
  /** One instance of `cue` finished (or was aborted). */
  release(cue: string): void;
  /** How many copies of `cue` are currently counted as sounding. */
  active(cue: string): number;
  /** Forget everything (view teardown / stop-all). */
  reset(): void;
}

export const DEFAULT_COOLDOWN_MS = 80;
export const DEFAULT_MAX_CONCURRENT = 3;

export function createCueGate(opts: CueGateOpts = {}): CueGate {
  const cooldownMs = opts.cooldownMs ?? DEFAULT_COOLDOWN_MS;
  const maxConcurrent = Math.max(1, opts.maxConcurrent ?? DEFAULT_MAX_CONCURRENT);
  const last = new Map<string, number>();
  const live = new Map<string, number>();

  return {
    tryPlay(cue, now) {
      const prev = last.get(cue);
      if (prev !== undefined && now - prev < cooldownMs) return false;
      const n = live.get(cue) ?? 0;
      if (n >= maxConcurrent) return false;
      last.set(cue, now);
      live.set(cue, n + 1);
      return true;
    },
    release(cue) {
      const n = live.get(cue) ?? 0;
      // Clamp at zero: a late `ended` event after reset() must not go negative and
      // permanently free up an extra slot.
      live.set(cue, Math.max(0, n - 1));
    },
    active(cue) { return live.get(cue) ?? 0; },
    reset() { last.clear(); live.clear(); },
  };
}

/**
 * Volume multiplier for a world sound heard `dist` pixels away.
 *
 * Full volume out to `full`, silent past `max`, and a squared falloff between —
 * squared rather than linear because equal-loudness perception is closer to it,
 * and because it keeps distant background scuffles well under the sound the
 * player caused themselves.
 */
export function distanceGain(dist: number, full: number, max: number): number {
  if (!(dist > full)) return 1;          // NaN-safe: unknown distance = full volume
  if (dist >= max) return 0;
  const k = 1 - (dist - full) / (max - full);
  return k * k;
}

/**
 * Choose between `cue`, `cue_2`, `cue_3`, … so repeats don't sound identical.
 *
 * `available` holds only the variants the loader has actually CONFIRMED exist —
 * an optimistic picker that guessed at `cue_2` and lost played nothing at all,
 * which is worse than a repeated sound. The base cue is always a candidate, so a
 * cue that ships without variants (or whose variants have not been generated yet)
 * simply always returns the base name.
 */
export function pickVariant(
  cue: string,
  available: ReadonlySet<string>,
  rnd: () => number = Math.random,
  maxVariant = 3,
): string {
  const options = [cue];
  for (let i = 2; i <= maxVariant; i++) {
    const name = `${cue}_${i}`;
    if (available.has(name)) options.push(name);
  }
  const i = Math.floor(rnd() * options.length);
  return options[Math.min(options.length - 1, Math.max(0, i))];
}
